import React from "react";
import PropTypes from "prop-types";
import { mediaProfileUrl, mediaOutletUrl } from "views_shared/app/javascript/utils/urls";
import MessageButton from "../../MessageButton";
import LocationMarkerIcon from "../../profiles/LocationMarkerIcon";
import SavedGroupsBookmark from "../../source_directory/SavedGroupsBookmark";
import { usePeerLastMessageSentAt } from "../../source_directory/LastMessagesContext/LastMessagesContext";
import formatLastMessageDate from "../../source_directory/formatLastMessageDate";
import SourceAvatar from "../../source_directory/SourceAvatar";
import HighlightAttribute, { attributeMatched } from "../../source_directory/HighlightAttribute";
// Excerpt is a plain "first N words, then Show more" block with nothing PR-specific about it; it
// simply happens to live where it was first written.
import Excerpt from "../../pr_profiles/card/Excerpt";
import canMessageMediaProfile from "./messageGating";

function PublicationsLine({ hit, publications }) {
  if (publications.length === 0) return null;

  return (
    <div>
      <div style={{ fontSize: 13, color: "#4d5357" }}>Writes for:</div>
      <div style={{ marginTop: 4, fontSize: 14, color: "#212528" }}>
        {publications.map((publication, index) => (
          <React.Fragment key={publication.slug || publication.name}>
            {index > 0 && ", "}
            {publication.slug ? (
              <a
                href={mediaOutletUrl({ slug: publication.slug, migrate_id: publication.migrate_id })}
                style={{ color: "#212528", textDecoration: "underline" }}
              >
                <HighlightAttribute hit={hit} attribute={["publications", index, "name"]} fallback={publication.name} />
              </a>
            ) : (
              <HighlightAttribute hit={hit} attribute={["publications", index, "name"]} fallback={publication.name} />
            )}
          </React.Fragment>
        ))}
      </div>
    </div>
  );
}

PublicationsLine.propTypes = {
  hit: PropTypes.instanceOf(Object).isRequired,
  publications: PropTypes.arrayOf(PropTypes.shape({ name: PropTypes.string, slug: PropTypes.string })).isRequired,
};

function MediaProfileCardGrid({ hit }) {
  const canMessage = canMessageMediaProfile(hit.id);
  const lastMessageDate = formatLastMessageDate(usePeerLastMessageSentAt(hit.id));
  const publications = hit.publications || [];

  return (
    <div
      className="animated faster fadeIn"
      style={{
        gridRow: "span 4",
        display: "grid",
        gridTemplateRows: "subgrid",
        borderRadius: 10,
        border: "1px solid #e5e7eb",
        overflow: "hidden",
        backgroundColor: "#fff",
      }}
    >
      {/* Row 1: Header */}
      <div style={{ display: "flex", alignItems: "flex-start", gap: 12, padding: "16px 16px 14px" }}>
        <SourceAvatar
          imageUrl={hit.profile_image_url}
          name={hit.name}
          initialsText={hit.initials}
          size={60}
          fontSize={18}
        />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontWeight: 700, fontSize: 16 }}>
            <a href={mediaProfileUrl({ slug: hit.slug, migrate_id: hit.migrate_id })} style={{ color: "#111827" }}>
              <HighlightAttribute hit={hit} attribute="name" fallback={hit.name} />
            </a>
          </div>
          {hit.title || hit.workplace_name ? (
            <div style={{ fontSize: 13, color: "#6b7280", marginTop: 2 }}>
              {/* workplace_name is faceting-only and never highlighted; title_at_workplace is the
                  searchable "<title> at <workplace>" field, so render it (highlighted) when it
                  matched, otherwise keep the linked title/workplace composition. */}
              {attributeMatched(hit, "title_at_workplace") ? (
                <HighlightAttribute hit={hit} attribute="title_at_workplace" />
              ) : (
                <>
                  {hit.title}
                  {hit.workplace_name && (
                    <>
                      {hit.title ? " at " : ""}
                      {hit.workplace_slug ? (
                        <a
                          href={mediaOutletUrl({ slug: hit.workplace_slug, migrate_id: hit.workplace_migrate_id })}
                          style={{ color: "#4d5357", textDecoration: "underline" }}
                        >
                          {hit.workplace_name}
                        </a>
                      ) : (
                        hit.workplace_name
                      )}
                    </>
                  )}
                </>
              )}
            </div>
          ) : null}
        </div>
        <SavedGroupsBookmark
          sourceableType="Id::Profile::MediaProfile"
          sourceableId={hit.id}
          sourceMeta={{ name: hit.name, imageUrl: hit.profile_image_url, slug: hit.slug }}
        />
      </div>

      {/* Row 2: Separator — full-width (no padding; overflow:hidden on card clips to border-radius) */}
      <div style={{ height: 1, backgroundColor: "#e9e9e9" }} />

      {/* Row 3: Content */}
      <div style={{ padding: "8px 16px 0" }}>
        {hit.location_string && (
          <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 14, color: "#212528" }}>
            <LocationMarkerIcon size={14} />
            {hit.location_string}
          </div>
        )}

        <PublicationsLine hit={hit} publications={publications} />

        {hit.bio && hit.bio.length > 0 && (
          <div style={{ marginTop: 10, fontSize: 13, color: "#4d5357" }}>
            <Excerpt text={hit.bio} maxWords={25} />
          </div>
        )}
      </div>

      {/* Row 4: CTA — always rendered to maintain 4-child count for subgrid */}
      {canMessage ? (
        <div style={{ padding: "12px 16px 16px" }}>
          <MessageButton
            profileId={hit.id}
            buttonLabel="Start a Conversation"
            extraButtonClasses="w-100 mb-0"
            openInboxThread
          />
          {lastMessageDate && (
            <div style={{ textAlign: "center", fontSize: 12, lineHeight: "17.4px", color: "#4d5357", marginTop: 10 }}>
              Last message sent {lastMessageDate}
            </div>
          )}
        </div>
      ) : (
        <div />
      )}
    </div>
  );
}

MediaProfileCardGrid.propTypes = {
  hit: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    slug: PropTypes.string.isRequired,
    name: PropTypes.string,
    bio: PropTypes.string,
    profile_image_url: PropTypes.string,
    initials: PropTypes.string,
    title: PropTypes.string,
    title_at_workplace: PropTypes.string,
    workplace_name: PropTypes.string,
    workplace_slug: PropTypes.string,
    location_string: PropTypes.string,
    publications: PropTypes.arrayOf(PropTypes.shape({ name: PropTypes.string, slug: PropTypes.string })),
  }).isRequired,
};

export default MediaProfileCardGrid;
